import type {
    BuildingType,
    BuildingZone,
    Planet,
    TickCheckpointEvent,
    TickCheckpointEventOf,
} from '@kaeliux/shared';

export function isPendingBuild(
    e: { eventType: string; cancelled?: boolean },
): e is TickCheckpointEventOf<'buildingBuild'> {
    return e.eventType === 'buildingBuild' && !e.cancelled;
}

export function pendingBuilds(planet: Planet | null | undefined): TickCheckpointEventOf<'buildingBuild'>[] {
    return ((planet?.tickCheckpoints ?? []) as TickCheckpointEvent[]).filter(isPendingBuild);
}

/** Group a planet's pending build events by buildingType, each sorted by endDate (chain order). */
export function groupPendingBuildsByBuilding(
    events: TickCheckpointEventOf<'buildingBuild'>[],
): Partial<Record<BuildingType, TickCheckpointEventOf<'buildingBuild'>[]>> {
    const groups: Partial<Record<BuildingType, TickCheckpointEventOf<'buildingBuild'>[]>> = {};
    for (const event of events) {
        const type = event.eventDetails.buildingType;
        (groups[type] ??= []).push(event);
    }
    for (const list of Object.values(groups)) {
        list?.sort((a, b) => a.endDate.getTime() - b.endDate.getTime());
    }
    return groups;
}

/**
 * Group a planet's pending build events by claimed plot (DS-28) — only
 * events for a level 0→1 construct carry a `plotId` at all, so this is how
 * an empty plot's progress ring/countdown is found (`PlanetSurface`),
 * separately from `groupPendingBuildsByBuilding`'s per-building groups used
 * for a built tile's upgrade progress.
 */
export function groupPendingBuildsByPlot(
    events: TickCheckpointEventOf<'buildingBuild'>[],
): Partial<Record<string, TickCheckpointEventOf<'buildingBuild'>[]>> {
    const groups: Partial<Record<string, TickCheckpointEventOf<'buildingBuild'>[]>> = {};
    for (const event of events) {
        const plotId = event.eventDetails.plotId;
        if (!plotId) continue;
        (groups[plotId] ??= []).push(event);
    }
    for (const list of Object.values(groups)) {
        list?.sort((a, b) => a.endDate.getTime() - b.endDate.getTime());
    }
    return groups;
}

/** Count pending build events per zone — used for the Planet page's zone badges. */
export function countPendingBuildsByZone(planet: Planet | null | undefined): Record<BuildingZone, number> {
    const counts: Record<BuildingZone, number> = { resource: 0, industry: 0, military: 0 };
    for (const event of pendingBuilds(planet)) {
        counts[event.eventDetails.zone] += 1;
    }
    return counts;
}
